import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe, NgIf} from '@angular/common';
import {select, Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getAuthState, getUserInfo} from '@shared/store/selectors/security.selector';
import {CallService} from '../call.service';
import {IncomingCallComponent} from '../incoming-call/incoming-call.component';
import {CallBarComponent} from '../call-bar/call-bar.component';
import {VideoCallCardComponent} from '../video-call-card/video-call-card.component';
import {RecordingConsentComponent} from '../recording-consent/recording-consent.component';
import {MissedCallBannerComponent} from '../missed-call-banner/missed-call-banner.component';
import {MissedCallService} from '../missed-call-banner/missed-call.service';

/**
 * The Calls feature as the shell mounts it, outside the router outlet so a call survives navigation: the incoming
 * overlay and the call bar (ITP-E-170-01), the video call card (ITP-E-182-01), recording consent (ITP-E-172-02) and the
 * missed-call banners with Do not disturb (ITP-E-171-01/04), plus the two asks of a page load — the call to rejoin
 * (ITP-E-170-06) and the missed calls not yet dismissed (ITP-E-171-01).
 *
 * ITP2-14993: a magic-link-only session (a share link, product code MAGIC-LINK-ONLY) never starts Calls. Its AccessId
 * lets through only the commands the link was made for, so GetActiveCallCommand, GetMissedCallsCommand and
 * GetCallPreferencesCommand came back "Access Id is invalid" — and a share-link visitor cannot take a call anyway.
 * Nothing mounts and nothing is asked until the access token is in the store: its claims are set with the token
 * itself, so the product codes are known before any of it starts. The feature list arrives later, by request, and
 * would race the loads.
 *
 * ITP-E-461-02: any magic-link session (the token carries access_id) skips Calls too. A questionnaire link
 * (ITP-VISITOR|NO-NAVBAR) or a subscription-workspace link (MPBase|ITP-VISITOR) also lists only its own commands but
 * carries no MAGIC-LINK-ONLY.
 */
@Component({
  selector: 'app-calls-shell',
  standalone: true,
  imports: [NgIf, AsyncPipe, IncomingCallComponent, CallBarComponent, VideoCallCardComponent, RecordingConsentComponent,
    MissedCallBannerComponent],
  templateUrl: './calls-shell.component.html'
})
export class CallsShellComponent implements OnInit, OnDestroy {
  static readonly magicLinkOnly = 'MAGIC-LINK-ONLY';

  /** True once a session that may take calls is signed in; never for a magic-link one. */
  readonly enabled$: Observable<boolean>;
  private readonly destroy$ = new Subject<void>();

  // The call services are resolved on use, so a session that never starts Calls never creates them.
  constructor(private securityStore: Store<SecurityState>,
              private injector: Injector) {
    // One read of the auth state: two selectors combined would see the new token with the old claims for an instant.
    this.enabled$ = this.securityStore.select(getAuthState).pipe(
      map(auth => !!auth?.accessToken && !auth.claims?.['access_id']
        && !CallsShellComponent.isMagicLinkOnly(auth.claims)),
      distinctUntilChanged()
    );
  }

  static isMagicLinkOnly(claims: Record<string, any> | null | undefined): boolean {
    const productCodes: string = claims?.['product_codes'] ?? '';
    return productCodes.split('|').includes(CallsShellComponent.magicLinkOnly);
  }

  ngOnInit(): void {
    // ITP-E-170-06: a refresh mid-call rejoins the call once the user is known; ITP-E-171-01: the missed calls not yet
    // dismissed or returned show again after a load. Each service asks only once however often the token is refreshed.
    this.enabled$.pipe(
      filter(enabled => enabled),
      take(1),
      switchMap(() => this.securityStore.pipe(select(getUserInfo), filter(userInfo => !!userInfo?.email), take(1))),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.injector.get(CallService).resumeActiveCall().catch(error =>
        console.warn('[calls] resuming the active call failed', error));
      this.injector.get(MissedCallService).load().catch(error =>
        console.warn('[calls] loading the missed calls failed', error));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
